import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { useAppDispatch, useAppSelector } from '../../hooks/storeHooks';
import { RootState } from '../../store';
import ExportTable from '../../components/common/export-table/ExportTable';
import Loader from '../common/Loader/Loader';
import DataTableGrid from '../../components/common/DataTableGrid';
import { Column } from 'react-table';
import { ReactComponent as NotepadIcon } from '../../images/note-pad.svg';
import { ReactComponent as PrintIcon } from '../../images/printer.svg';
import { ReactComponent as TrashIcon } from '../../images/trash.svg';
import { formatDateCell, formatStatusCell } from '../../utilities/tableCellHelpers';
import { formatDate } from '../../utilities/dateHelpers';
import '../well-completions/WellCompletions.scss';
import CreateInstallModal from './create-install/CreateInstallModal';
import { useToastService } from '../../components/toast/ToastService';
import { createTableSearchFilter } from '../../utilities/completionsUtils';
import {
  fetchAddInstallationData,
  fetchEditInstallationData,
  deleteInstallation,
} from '../../services/installationHistory/InstallationHistoryService';
import Modal from '../common/Modal/Modal';
import shutdownIcon from '../../images/shutdown.svg';

interface InstallationTableWidgetProps {
  widgetHeader: string;
  tableData: any[];
}

const InstallationTableWidget: React.FC<InstallationTableWidgetProps> = ({ widgetHeader, tableData }) => {
  const dispatch = useAppDispatch();
  const { showSuccessToast, showErrorToast } = useToastService();

  const sortedTableData = useMemo(() => {
    if (!tableData) return [];
    return [...tableData].sort((a, b) => (b.InstallNumber ?? 0) - (a.InstallNumber ?? 0));
  }, [tableData]);

  const loading = useAppSelector(
    (state: RootState) => state.installationHistorySlice?.installationHistory?.loading || false,
  );
  const selectedAssetId = useAppSelector((state) => state.assetGroups?.selectedAssetId);
  const wellInstallationPageData = useAppSelector((state: any) => state.testWellColumnsSlice.data?.widgets);
  const wellId = useAppSelector((state: RootState) => state.WellTestIdSlice?.data?.WellId);
  const deleteInstallationState = useAppSelector(
    (state: RootState) => state.installationHistorySlice?.deleteInstallation,
  );
  const deleteLoading = deleteInstallationState?.loading || false;
  const deleteError = deleteInstallationState?.error;

  const previousDeleteLoadingRef = useRef(false);

  useEffect(() => {
    if (previousDeleteLoadingRef.current && !deleteLoading) {
      if (deleteError) {
        showErrorToast('Delete failed', deleteError);
      } else {
        showSuccessToast('Installation has been successfully deleted.');
      }
    }

    previousDeleteLoadingRef.current = deleteLoading;
  }, [deleteLoading, deleteError, showErrorToast, showSuccessToast]);

  const wellInstallationData = useMemo(
    () => wellInstallationPageData?.find((obj: any) => obj.key === 'install-table'),
    [wellInstallationPageData],
  );

  const ColumnHeaders = useMemo(
    () => wellInstallationData?.widgetProperty?.widgetProperties?.columns || [],
    [wellInstallationData],
  );

  const searchableColumns = useMemo(() => ColumnHeaders.filter((col: any) => col.selected), [ColumnHeaders]);

  const searchFilter = useMemo(
    () =>
      createTableSearchFilter({
        searchableColumns,
        dateColumns: new Set([
          'InstallDate',
          'StartDate',
          'FailDate',
          'PullDate',
          'LastUpdated',
          'LastUpdatedString',
          'StartDateString',
          'InstallDateString',
          'PullDateString',
          'FailDateString',
        ]),
        statusColumns: new Set(['Status', 'Well Status']),
        htmlColumns: new Set(['ReportDownloadButtonsHtml', 'DocumentDownloadButtonsHtml']),
        checkboxColumns: new Set(['BlockUpdateOverride']),
      }),
    [searchableColumns],
  );

  const [searchTerm, setSearchTerm] = useState<string>('');
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingDeleteId, setPendingDeleteId] = useState<number | null>(null);
  const rPermission = { canExport: true };

  const handleEditInstall = useCallback(
    (id: number) => {
      if (wellId) {
        dispatch(fetchEditInstallationData({ wellId: wellId, id: id }));
        setIsEditMode(true);
        setIsCreateModalOpen(true);
      }
    },
    [wellId, dispatch],
  );

  const handleCloseCreateModal = useCallback(() => {
    setIsCreateModalOpen(false);
    setIsEditMode(false);
  }, []);

  const handleSaveCompletion = useCallback(() => {
    if (wellId) {
      // TODO : Implement save
    }
  }, [wellId, dispatch]);

  const handleDeleteInstall = useCallback(
    (id: number) => {
      if (!deleteLoading) {
        setPendingDeleteId(id);
        setShowConfirmModal(true);
      }
    },
    [deleteLoading],
  );

  const confirmDeleteInstall = useCallback(() => {
    if (pendingDeleteId !== null) {
      dispatch(deleteInstallation(pendingDeleteId.toString()));
      setShowConfirmModal(false);
      setPendingDeleteId(null);
    }
  }, [dispatch, pendingDeleteId]);

  const dynamicColumns: Column<any>[] = useMemo(() => {
    const baseColumns = [
      {
        Header: '',
        id: 'installation-actions',
        width: 80,
        Cell: ({ row }: { row: { original: any } }) => (
          <div className='tdt-action-cell'>
            <NotepadIcon onClick={() => handleEditInstall(row.original.Id)} />
            <PrintIcon
              onClick={() => console.log('Print:', row.original.WellId)}
              style={{ opacity: 0.4, cursor: 'not-allowed' }}
            />
            <TrashIcon
              onClick={() => handleDeleteInstall(row.original.Id)}
              style={{
                cursor: 'pointer',
              }}
            />
          </div>
        ),
      },
      {
        Header: 'Status',
        accessor: 'Status',
        id: 'Status',
        Cell: formatStatusCell,
      },
    ];

    const mappedColumns = ColumnHeaders.filter((col: any) => col.selected).map((col: any) => {
      // Custom cell for InstallDate, StartDate, FailDate, PullDate (format DD/MM/YYYY)
      if (
        [
          'InstallDate',
          'StartDate',
          'FailDate',
          'PullDate',
          'LastUpdated',
          'LastUpdatedString',
          'StartDateString',
          'InstallDateString',
          'PullDateString',
          'FailDateString',
        ].includes(col.key)
      ) {
        return {
          Header: col.label,
          accessor: col.key,
          id: col.key,
          Cell: formatDateCell,
        };
      }
      if (col.key === 'BlockUpdateOverride') {
        return {
          Header: col.label || 'Block updates',
          accessor: col.key,
          id: col.key,
          Cell: ({ value }: { value: any }) => (
            <input
              type='checkbox'
              checked={value === true || (typeof value === 'string' && value.toLowerCase() === 'true')}
              disabled
              style={{ cursor: 'not-allowed' }}
              className='card-table-checkbox'
            />
          ),
        };
      }
      // Custom cell for Reports and Documents columns to render HTML links as buttons
      if (col.key === 'ReportDownloadButtonsHtml' || col.key === 'DocumentDownloadButtonsHtml') {
        return {
          Header: col.label || (col.key === 'ReportDownloadButtonsHtml' ? 'Reports' : 'Downloads'),
          accessor: col.key,
          id: col.key,
          Cell: ({ value }: { value: any }) => {
            if (!value || value === null || value === undefined) return <span>-</span>;

            const htmlString = value.toString();

            const parts = htmlString.split('<a ').filter((part: string) => part.trim().length > 0);
            const links: Array<{ href: string; title: string; text: string }> = [];

            parts.forEach((part: string, index: number) => {
              if (index === 0 && !part.includes('href=')) return;

              const hrefMatch = part.match(/href=['"]([^'"]*)['"]/);
              const titleMatch = part.match(/title=['"]([^'"]*)['"]/);
              const textMatch = part.match(/<strong>([^<]*)<\/strong>/);

              if (hrefMatch && titleMatch && textMatch) {
                links.push({
                  href: hrefMatch[1],
                  title: titleMatch[1],
                  text: textMatch[1],
                });
              }
            });

            if (links.length === 0) {
              return <span>-</span>;
            }

            return (
              <div style={{ display: 'flex', gap: '4px' }}>
                {links.map((link, index) => (
                  <button
                    key={index}
                    className='btn btn-success btn-xs'
                    style={{
                      marginLeft: index > 0 ? '2px' : '0',
                      padding: '2px 6px',
                      fontSize: '11px',
                      backgroundColor: 'transparent',
                      color: 'var(--Success-Green-400, #32d583)',
                      border: '1px solid var(--Success-Green-400, #32d583)',
                      borderRadius: '3px',
                      cursor: 'pointer',
                    }}
                    title={link.title}
                  >
                    <strong>{link.text}</strong>
                  </button>
                ))}
              </div>
            );
          },
        };
      }
      // Default cell
      return {
        Header: col.label,
        accessor: col.key,
        id: col.key,
        Cell: (props: any) => {
          const value = props.value;
          if (value === null || value === undefined) return <span>-</span>;
          if (typeof value === 'object') return <span>{JSON.stringify(value)}</span>;
          return <span>{value}</span>;
        },
      };
    });

    const finalColumns = [...baseColumns, ...mappedColumns];
    return finalColumns;
  }, [ColumnHeaders]);

  // Export functionality
  const tableRowsForExport = useMemo(() => {
    const dataToExport = searchTerm ? searchFilter(sortedTableData, searchTerm) : sortedTableData;
    return (dataToExport || []).map((item) => ({ original: item }));
  }, [sortedTableData, searchTerm, searchFilter]);

  // Handle search input change
  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  // Handle create installations button click
  const handleAddInstallations = useCallback(() => {
    if (wellId) {
      dispatch(fetchAddInstallationData(wellId));
    }
    setIsEditMode(false);
    setIsCreateModalOpen(true);
  }, [wellId, dispatch]);

  // Generate export columns dynamically based on table columns
  const exportColumns = useMemo(() => {
    return dynamicColumns
      .filter((column) => column.id !== 'installation-actions') // Exclude action column from export
      .map((column) => {
        const header = typeof column.Header === 'string' ? column.Header : '';
        const accessor = column.accessor as string;
        const columnId = column.id || accessor;

        return {
          key: columnId,
          header: header,
          accessor: (row: any) => {
            // For regular accessors
            if (accessor) {
              const value = row.original?.[accessor];
              // Handle date columns
              if (
                [
                  'InstallDate',
                  'StartDate',
                  'FailDate',
                  'PullDate',
                  'LastUpdated',
                  'LastUpdatedString',
                  'StartDateString',
                  'InstallDateString',
                  'PullDateString',
                  'FailDateString',
                ].includes(columnId)
              ) {
                return formatDate(value);
              }

              // Handle HTML content by extracting text
              if (typeof value === 'string' && value.includes('<')) {
                // Simple HTML tag removal for export
                return value.replace(/<[^>]*>/g, '').trim() || '';
              }
              return value ?? '';
            }
            return '';
          },
        };
      });
  }, [dynamicColumns]);

  return (
    <>
      <div className='installation-main-container table-wrap'>
        <header className='cust-table-header'>
          <div
            className='cust-table-header-wrap'
            style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
          >
            <h1 className='table-header-title'>{widgetHeader}</h1>
            <button className='btn-prime' onClick={handleAddInstallations} disabled={loading || !wellId}>
              <span className='ico-add' />
              <span className='text-button'>Add installations</span>
            </button>
          </div>
        </header>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            margin: '16px 20px',
          }}
        >
          <div className='installation-export-section'>
            <ExportTable
              data={tableRowsForExport}
              fileName={`Installation-history-${selectedAssetId}-${formatDate(new Date()).replace(/,/g, '').replace(/ /g, '-')}`}
              sheetName='Installation history'
              columns={exportColumns}
              disabled={loading || !tableRowsForExport || tableRowsForExport.length === 0}
              rPermission={rPermission}
            />
          </div>
          <div className='flex pt-[7px] pr-[12px] pb-[6px] pl-[12px] items-center self-stretch shrink-0 flex-nowrap bg-[#001023] rounded-[8px] border border-solid border-[#4a5463] relative shadow-[0_1px_2px_0_rgba(16,24,40,0.05)] search-input w-[20%]'>
            <span className='ico-search' />
            <div className='relative flex w-full items-center'>
              <input
                type='text'
                placeholder='Search'
                value={searchTerm}
                onChange={handleSearchChange}
                style={{ border: 'none', background: 'transparent', outline: 'none' }}
                className="flex w-full h-[24px] pl-[8px] pr-[24px] font-['Mulish'] text-[16px] font-normal leading-[24px] text-[#b8c5cc] bg-transparent border-none"
              />

              {searchTerm && (
                <button
                  className='absolute right-[4px] text-[#b8c5cc] hover:text-white cursor-pointer text-[16px] leading-none bg-transparent border-none p-0 m-0'
                  onClick={() => setSearchTerm('')}
                  type='button'
                  aria-label='Clear search'
                >
                  ✕
                </button>
              )}
            </div>
          </div>
        </div>
        {deleteLoading && (
          <div className='installations-loader'>
            <Loader />
          </div>
        )}
        {loading ? (
          <div className='installation-loading-state'>
            <Loader />
          </div>
        ) : (
          <DataTableGrid
            data={sortedTableData}
            columns={dynamicColumns}
            searchTerm={searchTerm}
            customSearchFilter={searchFilter}
          />
        )}
      </div>

      <CreateInstallModal
        isOpen={isCreateModalOpen}
        onClose={handleCloseCreateModal}
        title={isEditMode ? 'Edit installation history' : 'Add new installation'}
        onSave={handleSaveCompletion}
        isEditMode={isEditMode}
      />

      <Modal
        open={showConfirmModal}
        setOpen={setShowConfirmModal}
        callbackOnConfirm={confirmDeleteInstall}
        title='Confirm delete'
        message='Are you sure you want to delete this install?'
        buttonText='Yes'
        Icon={shutdownIcon}
        hideCancelButton={false}
        buttonColor='red'
      />
    </>
  );
};

export default InstallationTableWidget;
